import {
  BellRing,
  Calendar,
  Check,
  CheckCircle,
  Hourglass,
  MapPin,
  PartyPopper,
  Siren,
  Star,
  User,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { StyleSheet } from "react-native";
import Confetti from "react-native-reanimated-confetti";
import { XStack } from "tamagui";
import { TagPill } from "../core/tags/TagPill";
import { TagColorsAvailable } from "../core/tags/types";
import {
  HelperType,
  type HelpItem,
  HelpItemRequiredType,
} from "../helper/HelperManager";
import { useHelper } from "../helper/useHelper";
import { usePlanNavigation } from "../plans/usePlanNavigation";
import { AnimationWiggle } from "../ui/AnimationWiggle";
import Blinking from "../ui/Blinking";
import { Block } from "../ui/Block";
import { Body, BodySkeleton, BodyTextarea } from "../ui/Body";
import { Box } from "../ui/Box";
import { Button } from "../ui/Button";
import { CenterContent } from "../ui/CenterContent";
import { Checkbox } from "../ui/Checkbox";
import { DotWizard } from "../ui/DotWizard";
import { EmojiPicker } from "../ui/EmojiPicker";
import { Group } from "../ui/Group";
import { IconBox } from "../ui/IconBox";
import { InfoRow, type InfoRowItemProps } from "../ui/InfoRow";
import { Input } from "../ui/InputNew";
import { Label, LabelSkeleton, LabelTextarea } from "../ui/Label";
import { Link } from "../ui/Link";
import { LoadingCircular } from "../ui/LoadingCircular";
import { Modal } from "../ui/Modal";
import { Pill, PillType } from "../ui/Pill";
import { PopoverHelp } from "../ui/PopoverHelp";
import { Screen } from "../ui/Screen";
import { Subtext, SubtextSkeleton } from "../ui/Subtext";
import { SubtextLink } from "../ui/SubtextLink";
import { TipsText } from "../ui/TipsText";
import { Title, TitleSkeleton, TitleTextarea } from "../ui/Title";
import { AvatarSize, AvatarSizeLookup } from "../ui/UserAvatar";
import { UserCircle } from "../ui/UserCircle";

const infoRowItems: InfoRowItemProps[] = [
  {
    text: "Johan",
    icon: User,
  },
  {
    text: "2 days ago",
    icon: Calendar,
  },
  {
    text: "Prague",
    icon: MapPin,
  },
];

export const DevScreen = observer(() => {
  const [emojiSheetOpen, setEmojiSheetOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const _navigation = usePlanNavigation();

  const [helperTest, setHelperTest] = React.useState(false);
  const items: HelpItem[] = React.useMemo(
    () => [
      {
        type: HelpItemRequiredType.Required,
        label: "First help item",
        isCompleted: () => {
          return true;
        },
      },

      {
        type: HelpItemRequiredType.Required,
        label: "Second help item",
        isCompleted: () => {
          return helperTest;
        },
      },

      {
        type: HelpItemRequiredType.Optional,
        label: "Third help item",
        isCompleted: () => {
          return false;
        },
      },

      {
        type: HelpItemRequiredType.Optional,
        label: "Fourth help item",
        isCompleted: () => {
          return false;
        },
      },
    ],
    [helperTest],
  );

  const helper = useHelper("1", HelperType.Poll, items);

  return (
    <>
      <Screen title="DevGuide">
        <Blinking>
          <Siren color="$danger" size={"$medium"} />
        </Blinking>

        {helper}
        <Button
          onPress={() => {
            setHelperTest(true);
          }}
        >
          Complete step 2
        </Button>

        <Box>
          <DotWizard
            dots={[
              { completed: true, step: 1 },
              { completed: true, step: 2 },
              { completed: false, step: 3 },
              { completed: false, icon: CheckCircle },
            ]}
          />

          <DotWizard
            dots={[
              { completed: false, icon: Hourglass, percent: 20 },
              { completed: false, icon: CheckCircle },
            ]}
          />

          <XStack gap="$small" flexWrap="wrap">
            {TagColorsAvailable.map((tagColor, i) => (
              <TagPill
                key={tagColor.color}
                tag={{
                  label: `Tag ${i + 1}`,
                  ...tagColor,
                  uid: `uid${i}`,
                  archived: i % 2 === 0,
                  dimmed: i % 4 === 0,
                  count: i > 2 ? i : undefined,
                  completed: i > 5 ? i - 2 : undefined,
                }}
              />
            ))}
          </XStack>

          <PopoverHelp description="This is used to to funny things with, just with care!" />
          <Button isLoading icon={Check}>
            Currently loading
          </Button>

          <Button onPress={() => setModalOpen((x) => !x)}>Toggle modal</Button>
          <Button onPress={() => setEmojiSheetOpen(true)}>
            Open emoji sheet
          </Button>
          <EmojiPicker
            sheetId="emoji"
            open={emojiSheetOpen}
            onOpenChange={(open: boolean) => setEmojiSheetOpen(open)}
            onSelect={(emoji) => {
              console.log("Emoji", emoji);
              setEmojiSheetOpen(false);
            }}
          />

          <Group>
            <Input
              type="border"
              multiline={false}
              placeholder="Placeholder"
              isLoading
            />

            <Input multiline={false} placeholder="One line" />
            <Input placeholder="Multiline" />
            <Input
              multiline={false}
              type="border"
              placeholder="One line border"
            />
            <Input type="border" placeholder="Multiline border" />
            <Input
              multiline={false}
              type="padded"
              placeholder="One line padded"
              attention
            />
            <Input type="padded" placeholder="Multiline padded" error />
          </Group>

          <Button type="secondary" onPress={() => {}}>
            Secondary Button
          </Button>

          <TipsText text="Vote for one option." />

          <Pill type={PillType.Success}>SUCCESS</Pill>
          <Pill type={PillType.Info}>INFO</Pill>
          <Pill type={PillType.Danger}>DANGER</Pill>

          <Title>Title</Title>
          <TitleSkeleton />
          <Label>Label</Label>
          <LabelSkeleton />
          <Body>Body</Body>
          <BodySkeleton />
          <Link to={"Home"}>Link</Link>
          <Subtext>Subtext</Subtext>
          <SubtextSkeleton />
          <SubtextLink to="Home">Subtext</SubtextLink>

          <AnimationWiggle>
            <BellRing color="$primary" />
          </AnimationWiggle>

          <CenterContent
            title={"Title"}
            subTitle={"Subtitle with desc"}
            icon={PartyPopper}
          />

          <Group>
            <TitleTextarea placeholder="Title input" />

            <LabelTextarea placeholder="Label textarea" />

            <BodyTextarea placeholder="Body textarea" />
          </Group>

          <LoadingCircular />

          <Group>
            <IconBox
              grouped
              label="My label"
              subText="My subtext"
              icon={Star}
            />
            <IconBox
              grouped
              label="My label"
              subText="My subtext"
              icon={Star}
            />
            <IconBox grouped label="My label" subText="My subtext" icon={Star}>
              <Body>My extra body</Body>
            </IconBox>
          </Group>

          <Group>
            <Body>First</Body>
            <Title>Second</Title>
            <Body>Third</Body>
          </Group>

          <Group>
            <Body>First</Body>
            <Body>Second</Body>
            <Body>Third</Body>
          </Group>

          <Checkbox checked={false} />

          <InfoRow items={infoRowItems} />

          <UserCircle name={"Johan"} />
          <UserCircle name={"Johan"} size={AvatarSizeLookup[AvatarSize.Tiny]} />
          <UserCircle name={"Thomas"} />
        </Box>

        <Box>
          <Title>Activity</Title>
          <Group>
            <Block
              grouped
              icon={<Star size={"$medium"} />}
              infoRowItems={infoRowItems}
            >
              <Label>Aktiviter</Label>
              <Body>Vilka datum kan folk?</Body>
            </Block>
            <Block
              grouped
              icon={<UserCircle name={"Johan"} />}
              infoRowItems={infoRowItems}
            >
              <Body>
                Vilka datum kan folk? Klicka i alla dagar ni kan så väljer vi en
                helg efter det
              </Body>
            </Block>
            <Block grouped infoRowItems={infoRowItems}>
              <Body>
                <Label>13 januari</Label> Vilka datum kan folk?
              </Body>
            </Block>
          </Group>
        </Box>

        <Modal
          modalId="dev"
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
        >
          <Title>Title in modal</Title>
        </Modal>
      </Screen>

      <Confetti count={100} origin={{ x: 500, y: 0 }} fadeOut />
    </>
  );
});

const _styles = StyleSheet.create({
  rowItem: {
    height: 100,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  text: {
    color: "white",
    fontSize: 24,
    fontWeight: "bold",
    textAlign: "center",
  },
});

export default DevScreen;
